import { api } from 'restaurantAdmin/api';

interface FloorPlanForAdminUpdate {
  id: string;
  name: string;
}

export interface FloorPlanTableImage {
  id: string;
  imageKey: string;
  position: number;
  url?: string;
}

export interface FloorPlanTableData {
  iconName: string;
  iconWidthScale: number;
  id?: string;
  left: number;
  maximumGuests: number;
  minimumGuests: number;
  name: string;
  orientation: number;
  publicName?: string | null;
  top: number;
  turnTime: number;
  description?: string | null;
  tags?: string[];
  images?: FloorPlanTableImage[];
}

export interface FloorPlanData {
  id: string;
  backgroundSrc: string;
  name: string;
  position: number;
  floorPlanTables: FloorPlanTableData[];
}

export const updateFloorPlans = (
  restaurantId: string,
  floorPlans: FloorPlanForAdminUpdate[],
) => api.put(`/restaurants/${restaurantId}/admin-floor-plans`, { floorPlans });

export const duplicateFloorPlan = (
  restaurantId: string,
  floorPlanId: string,
  { duplicateTables }: { duplicateTables: boolean },
) =>
  api.post<{ id: string }>(
    `/restaurants/${restaurantId}/floor-plans/${floorPlanId}/duplicate`,
    {
      duplicateTables,
    },
  );

export const getFloorPlan = ({
  restaurantId,
  floorPlanId,
}: {
  restaurantId: string;
  floorPlanId: string;
}) =>
  api.get<FloorPlanData>(
    `/admin/restaurants/${restaurantId}/floor-plans/${floorPlanId}`,
  );

/** Keep aligned with `floorPlanTableImageUploadConstants` in the API. */
export const FLOOR_PLAN_TABLE_IMAGE_UPLOAD_CONTENT_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
] as const;

export const getFloorPlanTableImageUploadContentType = (file: File): string => {
  if (
    file.type &&
    (FLOOR_PLAN_TABLE_IMAGE_UPLOAD_CONTENT_TYPES as readonly string[]).includes(
      file.type,
    )
  ) {
    return file.type;
  }
  throw new Error(
    'Unsupported image type. Please use a JPEG, PNG, WebP, or GIF file.',
  );
};

export const getFloorPlanTableImageUploadUrl = (
  restaurantId: string,
  contentType: string,
) =>
  api.get<{
    objectKey: string;
    signedUploadImageUrl: string;
    signedGetImageUrl: string;
    contentType: string;
  }>(
    `/restaurants/${restaurantId}/floor-plan-table-images/upload-url?contentType=${encodeURIComponent(contentType)}`,
  );

export const updateFloorPlanTables = ({
  restaurantId,
  floorPlanId,
  floorPlanName,
  deletedFloorPlanTableIds,
  updatedFloorPlanTables,
  createdFloorPlanTables,
}: {
  restaurantId: string;
  floorPlanId: string;
  floorPlanName: string;
  createdFloorPlanTables: FloorPlanTableData[];
  deletedFloorPlanTableIds: string[];
  updatedFloorPlanTables: FloorPlanTableData[];
}) =>
  api.post<FloorPlanTableData[]>(
    `/admin/restaurants/${restaurantId}/floor-plans/${floorPlanId}/update`,
    {
      floorPlanName,
      deletedFloorPlanTableIds,
      updatedFloorPlanTables,
      createdFloorPlanTables,
    },
  );
